import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {t} from 'i18next';
import {Box, Grid, Typography} from '@mui/material';
import DataImportsHeader from './top-header/DataImportsHeader';
import './ViewDataImports.scss';
import DataImportsCards from './data-imports-card/DataImportsCards';
import DataImportsFilter from './filter-panel/DataImportsFilter';
import {
  useICEDatasetTilePage,
  useICELoadDatasetView,
  useIngestionDataSets,
} from '../../../hooks/ingestions/useIngestions';
import {ICEDatasetTileItemType} from '../../../api/types/ingestionApi.type';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrButton from '../../common/arr-button/ArrButton';
import {RootState} from '../../../redux/store';

interface DataImportsProps {
  initialCount?: number;
  step?: number;
}

// Define as per existing sightline functionality
const sortingOrderMap: Record<string, string> = {
  LastModifiedOn: 'desc',
  LastModifiedBy: 'asc',
  DatasetCustodianName: 'asc',
};

const PAGE_SIZE = 12;
const DEFAULT_SORT_TYPE = 'LastModifiedOn';
const DEFAULT_DATA_SET_TYPE = '-1'; // -1 indicates all types

const ViewDataImports: React.FC<DataImportsProps> = () => {
  // Default data set type is -1 so all type of data load
  const [selectedDataSetType, setSelectedDataSetType] = useState<string>(
    DEFAULT_DATA_SET_TYPE
  );
  const [selectedSortType, setSelectedSortType] =
    useState<string>(DEFAULT_SORT_TYPE);
  const [searchInput, setSearchInput] = useState<string>('');
  // storing page no in ref because we are managing page load on load more click manually
  const currentDataSetPageNo = useRef<number>(1);
  const [tileDataSets, setTileDataSets] = useState<ICEDatasetTileItemType[]>(
    []
  );
  const [totalImportCount, setTotalImportCount] = useState(0);

  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );

  /** Load Dataset top document count, sorting and filter config */
  const {
    data: ingestionDataSets,
    isLoading: ingestionDatasetLoading,
    isError: haveIngestionDatasetError,
  } = useIngestionDataSets();

  // Fetch ICEDataset Grid Tile Data
  const {
    mutate: fetchICEDatasetTileData,
    isPending: isICEDatasetTilePageLoading,
  } = useICEDatasetTilePage();

  // Fetch ICEDataset total document count
  const {mutate: fetchICELoadDatasetView} = useICELoadDatasetView();

  useEffect(() => {
    // Fetch ICEDataset Tile Data on component mount
    if (
      !ingestionDatasetLoading &&
      !haveIngestionDatasetError &&
      ingestionDataSets?.iceProjectId
    ) {
      currentDataSetPageNo.current = 1;
      loadDataSetViewData();
    }
  }, [ingestionDataSets, ingestionDatasetLoading, haveIngestionDatasetError]);

  const getDatasetReqPayload = () => {
    return {
      iceProjectID: 0,
      datasetType: selectedDataSetType,
      datasetSearch: searchInput,
      displayView: 'tile',
      sortingColumn: selectedSortType,
      pageSize: PAGE_SIZE,
      pageNumber: currentDataSetPageNo.current,
      sortingOrder: sortingOrderMap[selectedSortType] || 'asc',
      encryptedICEProjectID: ingestionDataSets?.iceProjectId ?? '',
    };
  };

  useEffect(() => {
    currentDataSetPageNo.current = 1;
    loadDataSetViewData();
  }, [selectedSortType, selectedDataSetType, searchInput]);

  const loadDataSetViewData = () => {
    loadICEDatasetTileReq();
    fetchICELoadDatasetView(getDatasetReqPayload(), {
      onSuccess: (data) => {
        setTotalImportCount(data.totalDatasetCount);
      },
    });
  };

  const loadICEDatasetTileReq = () => {
    if (!ingestionDataSets?.iceProjectId) {
      return;
    }
    fetchICEDatasetTileData(getDatasetReqPayload(), {
      onSuccess: (data) => {
        if (currentDataSetPageNo.current > 1) {
          setTileDataSets((prevData) => [
            ...prevData,
            ...(data.datasets ?? []),
          ]);
        } else {
          setTileDataSets(data.datasets);
        }
      },
      onError: (err) => {
        console.error('Error fetching ICEDataset Tile Data:', err);
      },
    });
  };

  const renderFilterPanel = () => {
    return (
      <DataImportsFilter
        data={ingestionDataSets}
        totalImports={totalImportCount}
        onSortChange={setSelectedSortType}
        onDatasetTypeChange={setSelectedDataSetType}
        onSearchTextChange={setSearchInput}
        disableControls={haveIngestionDatasetError}
      />
    );
  };

  const renderCards = () => {
    if (isICEDatasetTilePageLoading && currentDataSetPageNo.current === 1) {
      return (
        <Box className="data-imports-loader-container">
          <ArrLoadingSpinner />
        </Box>
      );
    }

    if (tileDataSets.length === 0 || roleId === 2) {
      return (
        <Box className="data-imports-empty-container">
          <Typography
            className="typo-text body3_size message-label"
            tabIndex={0}
            data-testid={`view-data-imports-empty-data-set-msg`}
          >
            {t('dataImports.emptyDataSetMsg')}
          </Typography>
        </Box>
      );
    }

    return (
      <div
        className="data-imports-cards-container"
        data-testid={`view-data-imports-div`}
      >
        <Grid container spacing={2} className="data-imports-grid-container">
          {tileDataSets.map((item, index) => (
            <Grid key={index}>
              <DataImportsCards data={item} dataTestId={`${index}`} />
            </Grid>
          ))}
        </Grid>
      </div>
    );
  };

  const renderFooter = () => {
    if (isICEDatasetTilePageLoading && currentDataSetPageNo.current > 1) {
      return (
        <Box className="footer-container">
          <ArrLoadingSpinner />
        </Box>
      );
    }
    const totalPages = Math.ceil(totalImportCount / PAGE_SIZE);
    if (currentDataSetPageNo.current < totalPages) {
      return (
        <Box className="footer-container">
          <ArrButton
            variant="secondary"
            size="medium"
            dataTestId="load-more-data-sets-btn"
            onClick={() => {
              currentDataSetPageNo.current += 1;
              loadICEDatasetTileReq();
            }}
          >
            {t('common.loadMore')}
          </ArrButton>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box className="data-imports-container">
      <DataImportsHeader />
      {!ingestionDatasetLoading && (
        <>
          {renderFilterPanel()}
          {renderCards()}
          {renderFooter()}
        </>
      )}
      {ingestionDatasetLoading && <ArrLoadingSpinner overlay isOpen />}
    </Box>
  );
};

export default ViewDataImports;
